import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LandedMagicLink {
  token: string;
  email: string;
}

/**
 * AUTH.7. Holds an emailed sign-in link between the page opening it and a
 * person pressing "Continue to sign in".
 *
 * The link used to be spent the moment the page loaded. Mail security scanners
 * open links to check them, so on .mil addresses the scanner signed in first
 * and the member's own click then found the link already used. Now nothing is
 * spent until a person presses the button.
 *
 * Kept here rather than in the cognitoAuth store on purpose: that feature is
 * synced to sessionStorage, and a live sign-in token has no business being
 * written to storage. This lives in memory and is gone on reload, which is the
 * right outcome because the URL has been scrubbed by then too.
 *
 * Imports nothing from state, so the effects can depend on it without a cycle
 * through the login dialog.
 */
@Injectable({
  providedIn: 'root'
})
export class MagicLinkLandingService {
  private held: LandedMagicLink | null = null;

  /** A link that is ready to be offered, once sign-in status is known. */
  public readonly offer$ = new BehaviorSubject<LandedMagicLink | null>(null);

  /** The page opened with a link in its URL. Nothing is spent yet. */
  public hold(link: LandedMagicLink): void {
    this.held = link;
  }

  /**
   * Sign-in status is known. Offer the held link only to somebody who is not
   * already signed in: a member who already has a session opening an old link
   * has nothing to finish.
   */
  public release(isAuthenticated: boolean): void {
    const link = this.held;
    this.held = null;

    if (link && !isAuthenticated) {
      this.offer$.next(link);
    }
  }

  /** The offer has been shown; do not show it again. */
  public consumeOffer(): LandedMagicLink | null {
    const link = this.offer$.value;
    this.offer$.next(null);
    return link;
  }
}
